"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const oauth_1 = require("./oauth");
const netSuiteErrorFormat_1 = require("./netSuiteErrorFormat");
/**
 * Sleep function for retry delays
 */
const sleep = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
};
class NetSuiteAPIClient {
    subdomain;
    oauthParams;
    isProd;
    constructor(subdomain, oauthParams) {
        this.subdomain = subdomain;
        this.oauthParams = oauthParams;
        this.isProd = !subdomain.includes("sb2");
    }
    async request(endpoint, method, data, // Define the data type more specifically
    isRestlet, retryCount = 0, maxRetries = 10, delayMs = 70000, // 70 seconds
    requestLogOptions) {
        // Construct the base URL based on whether it's a Restlet or not
        const baseUrl = isRestlet
            ? `https://${this.subdomain}.restlets.api.netsuite.com`
            : `https://${this.subdomain}.suitetalk.api.netsuite.com`;
        // Check if method is GET and append data as query params
        let url = `${baseUrl}${endpoint}`;
        if (method.toUpperCase() === "GET" && data) {
            const queryParams = new URLSearchParams(data).toString();
            // Check if the endpoint already contains a '?'
            url += endpoint.includes("?") ? `&${queryParams}` : `?${queryParams}`;
        }
        const logEndpoint = method.toUpperCase() === "GET" && data
            ? `${endpoint}${endpoint.includes("?") ? "&" : "?"}${new URLSearchParams(data).toString()}`
            : endpoint;
        this.oauthParams.httpMethod = method;
        this.oauthParams.url = url;
        const authorizationHeader = (0, oauth_1.generateOAuthHeader)(this.oauthParams);
        // Prepare request options, excluding body for GET requests
        const options = {
            method,
            headers: {
                "Content-Type": "application/json",
                Authorization: authorizationHeader,
                Prefer: "transient",
            },
            body: method.toUpperCase() !== "GET" && data
                ? JSON.stringify(data)
                : undefined,
        };
        // Use redactBase64InJson when serializing the request body for the log
        // line so any inbound base64 payloads (e.g. file uploads) don't fill the
        // log with binary noise. The actual wire body sent to NetSuite is built
        // separately above (line 62-64) so this only affects logging.
        const bodyStr = method.toUpperCase() !== "GET" && data
            ? JSON.stringify(data, netSuiteErrorFormat_1.redactBase64InJson)
            : "";
        console.log(`[NetSuiteAPI] ${method} ${logEndpoint}` +
            (bodyStr
                ? `\n[NetSuiteAPI] request body (${bodyStr.length} chars): ${(0, netSuiteErrorFormat_1.truncateForLog)(bodyStr, 8000)}`
                : ""));
        if (method.toUpperCase() === "GET" && data) {
            console.log(`[NetSuiteAPI] wire URL: ${url}`);
        }
        try {
            const response = await fetch(url, options);
            if (response.status === 204) {
                const id = response.headers.get("Location")?.split("/").pop();
                return id ? { status: 204, id } : { status: 204 };
            }
            // Handle 429 (rate limit) errors with comprehensive retry logic
            if (response.status === 429) {
                const buffer = await response.arrayBuffer();
                const decoder = new TextDecoder("utf-8");
                const responseBody = decoder.decode(buffer);
                console.warn(`Rate limit exceeded (429). Response: ${responseBody}`);
                if (retryCount < maxRetries) {
                    console.log(`NetSuite API rate limit hit. Waiting ${delayMs}ms before retry ${retryCount + 1}/${maxRetries}`);
                    await sleep(delayMs);
                    // Retry the request with same parameters but incremented retry count
                    return this.request(endpoint, method, data, isRestlet, retryCount + 1, maxRetries, delayMs, requestLogOptions);
                }
                // If max retries exceeded, throw
                throw new Error(`HTTP 429 Rate Limit exceeded after ${maxRetries} attempts: ${responseBody}`);
            }
            if (!response.ok) {
                const buffer = await response.arrayBuffer();
                const decoder = new TextDecoder("utf-8");
                const responseBody = decoder.decode(buffer);
                const summary = (0, netSuiteErrorFormat_1.summarizeNetSuiteResponseBody)(responseBody, {
                    truncateDetails: false,
                });
                console.error(`[NetSuiteAPI] HTTP ${response.status} ${method} ${logEndpoint} | response ${responseBody.length} chars`);
                console.error(`[NetSuiteAPI] parsed summary: ${summary}`);
                console.error(`[NetSuiteAPI] raw body (full, ${responseBody.length} chars): ${responseBody}`);
                const msg = (0, netSuiteErrorFormat_1.buildNetSuiteHttpErrorMessage)(response.status, method, endpoint, responseBody);
                const error = new Error(msg);
                error.netSuiteHttpStatus = response.status;
                error.netSuiteMethod = method;
                error.netSuiteEndpoint = endpoint;
                error.netSuiteResponseBody = responseBody;
                error.netSuiteSummary = summary;
                // Don't retry 400 Bad Request errors - these are client errors that won't be fixed by retrying
                // This allows our error handling code to catch and handle them immediately
                if (response.status === 400) {
                    console.log(`[NetSuiteAPI] 400 Bad Request error detected - skipping retries to allow immediate error handling`);
                    throw error;
                }
                // Don't retry 404 Not Found - this is a definitive "the record does not exist"
                // answer from NetSuite. Retrying it (the historical default) burned ~12 minutes
                // per missing-record probe (10 attempts * 70s backoff) and was the root cause
                // of the financial-step probe stalls in MULTI_DAY transfers. Idempotency probes
                // in subscriptionTransferLogic now use SuiteQL (which returns 200 + 0 rows on a
                // miss) -- this short-circuit is defense-in-depth for any other caller that
                // legitimately expects a "not found" answer (e.g., GET /<type>/eid:<id>).
                if (response.status === 404) {
                    console.log(`[NetSuiteAPI] 404 Not Found detected - skipping retries (definitive missing-record response)`);
                    throw error;
                }
                throw error;
            }
            const json = await response.json();
            const serialized = JSON.stringify(json, netSuiteErrorFormat_1.redactBase64InJson);
            if (requestLogOptions?.logFullResponse) {
                console.log(`[NetSuiteAPI] ${method} ${logEndpoint} -> ${response.status} OK, full response (${serialized.length} chars): ${serialized}`);
            }
            else {
                const preview = (0, netSuiteErrorFormat_1.truncateForLog)(serialized, 4000);
                console.log(`[NetSuiteAPI] ${method} ${logEndpoint} -> ${response.status} OK, response (${preview.length} chars preview): ${preview}`);
            }
            return json;
        }
        catch (error) {
            // Check if this is a 400 error that we should not retry
            if (error instanceof Error &&
                (error.message.includes("Status: 400") ||
                    error.message.includes("NetSuite 400 ") ||
                    error.netSuiteHttpStatus === 400)) {
                console.log(`[NetSuiteAPI] 400 Bad Request error - not retrying`);
                throw error;
            }
            // Check if this is a 404 error that we should not retry
            // (definitive "record does not exist" answer; retrying it just wastes ~12 min)
            if (error instanceof Error &&
                (error.message.includes("Status: 404") ||
                    error.message.includes("NetSuite 404 ") ||
                    error.netSuiteHttpStatus === 404)) {
                console.log(`[NetSuiteAPI] 404 Not Found - not retrying`);
                throw error;
            }
            // Retry all other errors if we have retries left
            if (retryCount < maxRetries) {
                const detail = error instanceof Error ? error.message : String(error);
                console.error(`[NetSuiteAPI] ${method} ${endpoint} failed (attempt ${retryCount + 1}/${maxRetries}): ${detail}`);
                console.log(`[NetSuiteAPI] Retrying after ${delayMs}ms...`);
                await sleep(delayMs);
                // Retry the request
                return this.request(endpoint, method, data, isRestlet, retryCount + 1, maxRetries, delayMs, requestLogOptions);
            }
            // Don't wrap retry errors that have already been handled
            if (error instanceof Error &&
                (error.message.includes("Rate Limit") ||
                    (error.message.includes("after") && error.message.includes("attempts")))) {
                throw error;
            }
            // Preserve full NetSuite / fetch error after all retries (avoid generic wrapper that drops body)
            if (error instanceof Error) {
                console.error(`[NetSuiteAPI] Giving up after ${retryCount + 1} attempts: ${method} ${endpoint}`);
                throw error;
            }
            throw new Error(`NetSuite request failed after ${retryCount + 1} attempts: ${String(error)}`);
        }
    }
    async get(endpoint, data, requestLogOptions) {
        return this.request(endpoint, "GET", data, false, 0, 10, 70000, requestLogOptions);
    }
    async post(endpoint, data, requestLogOptions) {
        return this.request(endpoint, "POST", data, false, 0, 10, 70000, requestLogOptions);
    }
    async patch(endpoint, data) {
        return this.request(endpoint, "PATCH", data);
    }
    async delete(endpoint) {
        return this.request(endpoint, "DELETE");
    }
    async suiteql(query) {
        return this.request("/services/rest/query/v1/suiteql", "POST", {
            q: query,
        });
    }
    async restlet(id, deployId, method, data) {
        const endpoint = `/app/site/hosting/restlet.nl?script=${id}&deploy=${deployId}`;
        return this.request(endpoint, method, data, true);
    }
    /**
     * Call a Suitelet and return binary content as base64 string
     * Used for downloading files that exceed the 10MB getContents() limit
     *
     * @param scriptId - The Suitelet script ID
     * @param deployId - The Suitelet deployment ID
     * @param params - Query parameters to pass to the Suitelet
     * @returns Promise with base64 encoded file content
     */
    async suiteletDownloadFile(scriptId, deployId, fileId) {
        // Construct the Suitelet URL (note: scriptlet.nl, not restlet.nl)
        const baseUrl = `https://${this.subdomain}.restlets.api.netsuite.com`;
        const endpoint = `/app/site/hosting/scriptlet.nl?script=${scriptId}&deploy=${deployId}&fileId=${fileId}`;
        const url = `${baseUrl}${endpoint}`;
        console.log(`[Suitelet] Downloading large file via Suitelet: fileId=${fileId}`);
        // Generate OAuth header
        this.oauthParams.httpMethod = "GET";
        this.oauthParams.url = url;
        const authorizationHeader = (0, oauth_1.generateOAuthHeader)(this.oauthParams);
        const options = {
            method: "GET",
            headers: {
                Authorization: authorizationHeader,
            },
        };
        try {
            const response = await fetch(url, options);
            if (!response.ok) {
                // Try to get error message from response
                const contentType = response.headers.get("Content-Type") || "";
                if (contentType.includes("application/json")) {
                    const errorJson = await response.json();
                    console.error(`[Suitelet] Error response:`, errorJson);
                    return {
                        success: false,
                        error: errorJson.error || `HTTP ${response.status}`
                    };
                }
                const errorText = await response.text();
                console.error(`[Suitelet] Error response: ${response.status} - ${errorText}`);
                return {
                    success: false,
                    error: `HTTP ${response.status}: ${errorText.substring(0, 200)}`
                };
            }
            // Check if response is JSON (error) or binary (file content)
            const contentType = response.headers.get("Content-Type") || "";
            const contentDisposition = response.headers.get("Content-Disposition") || "";
            // Extract filename from Content-Disposition if available
            let fileName = "";
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch) {
                fileName = filenameMatch[1].replace(/['"]/g, '');
            }
            if (contentType.includes("application/json")) {
                // This is an error response from the Suitelet
                const errorJson = await response.json();
                console.error(`[Suitelet] JSON error response:`, errorJson);
                return {
                    success: false,
                    error: errorJson.error || "Unknown error from Suitelet"
                };
            }
            // Get binary content and convert to base64
            const arrayBuffer = await response.arrayBuffer();
            const buffer = Buffer.from(arrayBuffer);
            const base64Content = buffer.toString("base64");
            console.log(`[Suitelet] File downloaded successfully: ${fileName || 'unknown'}, size: ${buffer.length} bytes, base64 length: ${base64Content.length}`);
            return {
                success: true,
                base64Content,
                contentType,
                fileName
            };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error(`[Suitelet] Network/fetch error: ${errorMessage}`);
            return {
                success: false,
                error: `Network error: ${errorMessage}`
            };
        }
    }
}
exports.default = NetSuiteAPIClient;
